import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStreemLogin } from "../lib/api";
import { useAuthStore } from "../store/useAuthStore";
import { toast } from "../lib/use-toast";
import { Loader2 } from "lucide-react";

/**
 * SSO Callback Page
 *
 * Handles the OAuth callback from SSO providers (Azure, Okta, SAML, etc.)
 * Exchanges the authorization code for access/refresh tokens
 *
 * Flow:
 * 1. SSO provider redirects to /sso/auth?code=xxx
 * 2. Extract code from URL parameters
 * 3. Retrieve username from auth store (persisted in localStorage)
 * 4. Call /auth/login with username and code
 * 5. Backend exchanges code for tokens
 * 6. Navigate to /workflows on success
 */
const SSOCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mutateAsync: login, isPending } = useStreemLogin();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    // If already authenticated, redirect to workflows
    if (isAuthenticated) {
      navigate("/workflows");
      return;
    }

    const handleCallback = async () => {
      // Extract authorization code from URL
      const code = searchParams.get("code");

      if (!code) {
        toast({
          variant: "destructive",
          title: "SSO Error",
          description: "No authorization code received from SSO provider.",
        });
        navigate("/");
        return;
      }

      // Retrieve username from auth store (persisted in localStorage)
      const storedUsername = useAuthStore.getState().pendingUsername;

      if (!storedUsername) {
        toast({
          variant: "destructive",
          title: "SSO Error",
          description: "Session expired. Please try logging in again.",
        });
        navigate("/");
        return;
      }

      try {
        // Exchange code for tokens
        const response = await login({
          username: storedUsername,
          password: null,
          code,
        });

        if (response.data) {
          // Success - clean up pending username
          useAuthStore.getState().setPendingUsername(null);
          navigate("/workflows");
        } else if (response.errors && response.errors.length > 0) {
          // API returned errors
          useAuthStore.getState().setPendingUsername(null);
          toast({
            variant: "destructive",
            title: "SSO Login Failed",
            description: response.errors[0].message,
          });
          navigate("/");
        }
      } catch (error: any) {
        console.error("SSO login error:", error);
        useAuthStore.getState().setPendingUsername(null);

        // Extract error message from response
        const errorMessage =
          error?.response?.data?.errors?.[0]?.message ||
          error?.message ||
          "SSO authentication failed. Please try again.";

        toast({
          variant: "destructive",
          title: "Error",
          description: errorMessage,
        });
        navigate("/");
      }
    };

    handleCallback();
  }, [searchParams, navigate, login, isAuthenticated]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="flex flex-col items-center space-y-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="text-lg text-muted-foreground">
          {isPending ? "Completing sign in..." : "Processing SSO callback..."}
        </p>
      </div>
    </div>
  );
};

export default SSOCallbackPage;
